import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from '../api/axios';
import { API_ENDPOINTS } from "../api/endpoint";
import { toast } from "react-toastify";

const initialState = {
    paypalSettings: null,
    loading: false,
    error: null,
    updateLoading: false,
    updateError: null,
};

export const adminGetPaypalSettings = createAsyncThunk(
    'paypal/adminGetPaypalSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios({
                url: API_ENDPOINTS.ADMIN_GET_PAYPAL_SETTINGS,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.error || 'Failed to fetch PayPal settings');
        }
    }
);

export const adminUpdatePaypalSettings = createAsyncThunk(
    'paypal/adminUpdatePaypalSettings',
    async (settings, { rejectWithValue }) => {
        try {
            const response = await axios({
                url: API_ENDPOINTS.ADMIN_UPDATE_PAYPAL_SETTINGS,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: settings,
            });
            toast.success('PayPal settings updated successfully!');
            return response.data;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || 'Failed to update PayPal settings';
            toast.error(errorMsg);
            return rejectWithValue(errorMsg);
        }
    }
);

export const createPaypalOrder = createAsyncThunk(
    'paypal/createPaypalOrder',
    async (orderData, { rejectWithValue }) => {
        try {
            const response = await axios({
                url: API_ENDPOINTS.CREATE_PAYPAL_ORDER,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: orderData,
            });
            return response.data;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || 'Failed to create PayPal order';
            toast.error(errorMsg);
            return rejectWithValue(errorMsg);
        }
    }
);

export const verifyPaypalPayment = createAsyncThunk(
    'paypal/verifyPaypalPayment',
    async (paymentData, { rejectWithValue }) => {
        try {
            const response = await axios({
                url: API_ENDPOINTS.VERIFY_PAYPAL_PAYMENT,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: paymentData,
            });
            return response.data;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || 'Failed to verify PayPal payment';
            toast.error(errorMsg);
            return rejectWithValue(errorMsg);
        }
    }
);

const paypalSettingsSlice = createSlice({
    name: 'paypalSettings',
    initialState,
    reducers: {
        clearPaypalError: (state) => {
            state.error = null;
            state.updateError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get PayPal Settings
            .addCase(adminGetPaypalSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(adminGetPaypalSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.paypalSettings = action.payload;
            })
            .addCase(adminGetPaypalSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update PayPal Settings
            .addCase(adminUpdatePaypalSettings.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(adminUpdatePaypalSettings.fulfilled, (state, action) => {
                state.updateLoading = false;
                state.paypalSettings = action.payload;
            })
            .addCase(adminUpdatePaypalSettings.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            });
    },
});

export const { clearPaypalError } = paypalSettingsSlice.actions;
export default paypalSettingsSlice.reducer;

